import React, { useEffect, useState } from 'react';
import api from '../lib/api';

/** Inline sparkline (SVG) — terminates in a pulsing green dot. No text label. */
function Sparkline({ points, dotColor = '#22C55E', color = '#C9A24B', width = 320, height = 90 }) {
  // points: [{ t, pct }] or empty
  if (!points || points.length === 0) {
    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <line x1="0" y1={height - 8} x2={width} y2={height - 8} stroke="rgba(201,162,75,0.16)" strokeDasharray="3 4" />
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#A99563" fontFamily="JetBrains Mono" fontSize="11">No closed trades yet</text>
      </svg>
    );
  }
  // We always plot from 0 → cumulative, so include a leading 0 anchor.
  const series = [{ pct: 0 }, ...points];
  const ys = series.map((p) => p.pct);
  const min = Math.min(0, ...ys);
  const max = Math.max(0, ...ys);
  const range = (max - min) || 1;
  const dx = width / (series.length - 1 || 1);
  const pts = series.map((p, i) => [
    i * dx,
    height - 14 - ((p.pct - min) / range) * (height - 28),
  ]);
  const path = pts.map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(' ');
  const last = pts[pts.length - 1];
  const zeroY = height - 14 - ((0 - min) / range) * (height - 28);
  const gradId = `spark-${color.replace('#', '')}`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="0" y1={zeroY} x2={width} y2={zeroY} stroke="rgba(201,162,75,0.12)" strokeDasharray="2 4" />
      <path d={`${path} L ${width} ${height} L 0 ${height} Z`} fill={`url(#${gradId})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Pulsing dot at the current value — soft outer glow + crisp inner dot */}
      <circle cx={last[0]} cy={last[1]} r="8" fill={dotColor} opacity="0.25">
        <animate attributeName="r" values="6;12;6" dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.35;0.05;0.35" dur="1.6s" repeatCount="indefinite" />
      </circle>
      <circle cx={last[0]} cy={last[1]} r="3.5" fill={dotColor}>
        <animate attributeName="opacity" values="1;0.6;1" dur="1.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

function StatBlock({ label, value, testid }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <div className="text-[9px] tracking-[0.25em] font-mono text-[#A99563] uppercase">{label}</div>
      <div className="mt-0.5 font-mono text-base sm:text-lg font-semibold text-[#F1ECDF] tabular-nums truncate">{value}</div>
    </div>
  );
}

function FeedRow({ ev }) {
  const isClosed = ev.kind === 'closed';
  // For closed trades, color the WHOLE row BRIGHT green/red based on outcome.
  // For open trades, keep gold/neutral.
  let rowColor = '#CDBE94'; // open/neutral
  let pctText = '';
  if (isClosed) {
    const p = typeof ev.pct === 'number' ? ev.pct : null;
    if (p !== null) {
      const isWin = p >= 0;
      // Bright greens / reds across the whole row.
      rowColor = isWin ? '#22C55E' : '#EF4444';
      pctText = `${isWin ? '+' : '−'}${Math.abs(p).toFixed(2)}%`;
    }
  }
  // 4-column grid that mirrors the stat row above (TRADES | OPEN | CLOSED | WIN RATE).
  return (
    <div
      className="grid grid-cols-4 items-center gap-3 text-[11.5px] py-1 font-mono tabular-nums"
      style={{ color: rowColor }}
    >
      {/* TRADES column — symbol */}
      <span className="truncate font-semibold" data-testid="feed-symbol">{ev.symbol}</span>
      {/* OPEN column — 'OPENED' if opened, blank if closed */}
      <span className="text-[10px] uppercase tracking-wider">
        {!isClosed ? 'opened' : ''}
      </span>
      {/* CLOSED column — 'CLOSED' if closed, blank if open */}
      <span className="text-[10px] uppercase tracking-wider">
        {isClosed ? 'closed' : ''}
      </span>
      {/* WIN RATE column — signed pct for closed, blank for open */}
      <span className="text-[11px] font-semibold">{pctText}</span>
    </div>
  );
}

/**
 * Shared "Live Today" panel — used by the homepage AND the user dashboard.
 * Data source is the bot's signal logs (counted once per unique signal).
 *
 * Variant `compact` (homepage) packs everything tight in one column.
 * Variant `dashboard` adds Best/Worst symbol to the stat row.
 *
 * `dataSource`:
 *   - 'public'   (default) -> /api/public/live_today (all bot signals today)
 *   - 'personal' -> /api/user/today (only signals THIS user got filled on)
 */
export default function LiveTodayPanel({ refreshMs = 30000, variant = 'compact', dataSource = 'public' }) {
  const [data, setData] = useState({
    totals: { pct: 0, usd: 0 },
    stats: { trades: 0, open: 0, closed: 0, win_rate_pct: 0, best_symbol: null, worst_symbol: null },
    chart: [],
    feed: [],
  });

  useEffect(() => {
    let dead = false;
    const endpoint = dataSource === 'personal' ? '/user/today' : '/public/live_today';
    const tick = async () => {
      try {
        const { data: d } = await api.get(endpoint);
        if (!dead) setData(d);
      } catch (_) { /* keep last */ }
    };
    tick();
    const id = setInterval(tick, refreshMs);
    return () => { dead = true; clearInterval(id); };
  }, [refreshMs, dataSource]);

  const { totals, stats, chart, feed } = data;
  const pct = totals?.pct ?? 0;
  const up = pct > 0; const down = pct < 0;
  const pctCls = up ? 'pnl-positive' : down ? 'pnl-negative' : 'text-[#F1ECDF]';
  const pctStr = `${up ? '+' : down ? '−' : ''}${Math.abs(pct).toFixed(2)}%`;

  const dotColor = up ? '#22C55E' : down ? '#EF4444' : '#E8C874';

  return (
    <div
      data-testid="live-today-panel"
      className="bg-[#101012] border border-[rgba(201,162,75,0.16)] rounded-md p-4 flex flex-col h-full overflow-hidden"
    >
      {/* Header — live dot + label + big totals */}
      <div className="flex items-start justify-between gap-3 shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7FC58C] dot-pulse" />
            <span className="text-[10px] tracking-[0.3em] font-mono text-[#A99563] uppercase">Live today</span>
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span data-testid="live-pct" className={`font-mono font-bold text-2xl sm:text-3xl tabular-nums ${pctCls}`}>{pctStr}</span>
          </div>
        </div>
      </div>

      {/* Chart — pulsing dot only, no text annotation */}
      <div className="mt-2 shrink-0">
        <Sparkline points={chart} dotColor={dotColor} color={down ? '#E2706F' : '#C9A24B'} height={variant === 'dashboard' ? 90 : 60} />
      </div>

      {/* Stat row — always 4 cols so the feed rows below line up under each header */}
      <div
        className="mt-2 grid grid-cols-4 gap-3 pt-2 border-t border-[rgba(201,162,75,0.16)] shrink-0"
        data-testid="live-stats"
      >
        <StatBlock label="TRADES" value={stats.trades} testid="live-stat-trades" />
        <StatBlock label="OPEN" value={stats.open} testid="live-stat-open" />
        <StatBlock label="CLOSED" value={stats.closed} testid="live-stat-closed" />
        <StatBlock label="WIN RATE" value={`${stats.win_rate_pct}%`} testid="live-stat-winrate" />
      </div>
      {variant === 'dashboard' && (
        <div className="mt-2 grid grid-cols-2 gap-3 pt-2 shrink-0">
          <StatBlock label="BEST" value={stats.best_symbol || '—'} testid="live-stat-best" />
          <StatBlock label="WORST" value={stats.worst_symbol || '—'} testid="live-stat-worst" />
        </div>
      )}

      {/* Feed — flex-grows; overflow-y-auto so longer days don't break the layout */}
      <div className="mt-2 pt-2 border-t border-[rgba(201,162,75,0.16)] flex-1 overflow-y-auto" data-testid="live-recent">
        {feed && feed.length ? (
          feed.slice(0, 5).map((ev, i) => <FeedRow key={`${ev.t}-${i}`} ev={ev} />)
        ) : (
          <p className="text-[11px] text-[#A99563]">No bot signals yet today.</p>
        )}
      </div>
    </div>
  );
}
